import { useMutation } from '@tanstack/react-query';
import type { ConfigCustomFieldDto } from 'korint-sdk';
import type { Field } from 'korint-sdk/react';
import { CheckIcon, SquarePenIcon, XIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button } from '../../ui/button';
import { Group, Stack } from '@folio/components/ui/flex';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@folio/components/ui/table';
import { Form } from '../../forms/form';
import { FormBuilderFields } from '../../forms/form-builder/FormBuilder';
import {
  buildPayloadFromFields,
  resolveFieldShortcuts,
} from '../../forms/form-builder/form-fields-builders';
import { getResolver } from '../../forms/form-builder/getZodResolverFromFields';
import type { EntityValue, ArrayValue } from './InformationCard.type';
import { InformationFieldCell } from './InformationFieldCell';
import { InformationFieldWrapper } from './InformationFieldWrapper';

function InlineArrayEditor({
  field,
  rows,
  onSave,
  onCancel,
}: {
  field: Field;
  rows: ArrayValue;
  onSave: (rows: ArrayValue) => Promise<void> | void;
  onCancel: () => void;
}) {
  const form = useForm<Record<string, unknown>>({
    defaultValues: { [field.name]: rows },
    resolver: getResolver([field]),
  });

  const { mutate, isPending, error } = useMutation({
    mutationFn: async (values: Record<string, unknown>) => {
      const [customField] = buildPayloadFromFields([field], values).customFields as Array<{
        value: ArrayValue | null;
      }>;
      await onSave(customField?.value ?? []);
    },
  });

  return (
    <Form {...form}>
      <Stack gap={2}>
        <FormBuilderFields fields={[field]} />
        <Group gap={2}>
          <Button
            name="validate-inline-change"
            variant="outline"
            size="sm"
            loading={isPending}
            onClick={form.handleSubmit(values => mutate(values))}
          >
            <CheckIcon className="w-4 h-4" />
          </Button>
          <Button
            name="cancel-inline-change"
            variant="outline"
            size="sm"
            loading={isPending}
            onClick={onCancel}
          >
            <XIcon className="w-4 h-4" />
          </Button>
        </Group>
        {error && <div className="text-sm font-medium text-destructive">{error.message}</div>}
      </Stack>
    </Form>
  );
}

export function InformationArrayField({
  fieldKey,
  productId,
  fieldConfig,
  value,
  editionMode,
  redirectToForm,
  updateEntity,
}: {
  fieldKey: string;
  productId?: string;
  fieldConfig: ConfigCustomFieldDto;
  value: unknown;
  editionMode: 'INLINE' | 'FORM' | 'DISABLED';
  redirectToForm?: () => Promise<void> | void;
  updateEntity: (key: string, value: EntityValue) => Promise<void> | void;
}) {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const rows = Array.isArray(value) ? (value as ArrayValue) : [];
  const columns = fieldConfig.columns ?? [];

  const editableField = useMemo(() => {
    const [field] = resolveFieldShortcuts([fieldKey], [fieldConfig]);
    return field ? ({ ...field, label: '' } as Field) : undefined;
  }, [fieldKey, fieldConfig]);

  const formatCell = (cell: ArrayValue[number][string] | undefined) =>
    typeof cell === 'boolean' ? t(`options.${cell}`) : (cell ?? '');

  return (
    <InformationFieldWrapper>
      <InformationFieldCell className="[&:not(:first-child)]:pl-0" colSpan={2}>
        <Stack gap={2}>
          <Group justify="between" gap={2}>
            <span className="font-semibold">
              {fieldConfig.display?.label ??
                t([
                  ...(productId ? [`${productId}.fields.${fieldKey}.label`] : []),
                  `fields.${fieldKey}.informationField.label`,
                  `fields.${fieldKey}.label`,
                ])}
            </span>
            {editionMode === 'FORM' && (
              <Button
                name="mta-edition"
                variant="ghost"
                size="sm"
                icon={<SquarePenIcon className="w-4 h-4" />}
                onClick={redirectToForm}
              >
                {t('informationField.editContract')}
              </Button>
            )}
            {editionMode === 'INLINE' && !isEditing && editableField && (
              <Button
                name="edit-inline-field"
                variant="ghost"
                size="sm"
                onClick={() => setIsEditing(true)}
              >
                <SquarePenIcon className="w-4 h-4" />
              </Button>
            )}
          </Group>
          {isEditing && editableField ? (
            <InlineArrayEditor
              field={editableField}
              rows={rows}
              onCancel={() => setIsEditing(false)}
              onSave={async newRows => {
                await updateEntity(fieldKey, newRows);
                setIsEditing(false);
              }}
            />
          ) : (
            rows.length > 0 && (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead />
                    {columns.map(({ key }) => (
                      <TableHead key={key}>
                        {fieldConfig.display?.columnLabels?.[key] ??
                          t(`fields.${fieldKey}.columns.${key}`)}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row, index) => (
                    // biome-ignore lint/suspicious/noArrayIndexKey: rows are positional, no stable id
                    <TableRow key={index}>
                      <TableCell className="whitespace-nowrap font-medium">
                        {t([`fields.${fieldKey}.rowLabel`, 'arrayField.rowLabel'], {
                          index: index + 1,
                        })}
                      </TableCell>
                      {columns.map(({ key }) => (
                        <TableCell key={key}>{formatCell(row[key])}</TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )
          )}
        </Stack>
      </InformationFieldCell>
    </InformationFieldWrapper>
  );
}
